/**
 * External Imports
 */
import { createApi } from '@reduxjs/toolkit/query/react';
/**
 * Internal Imports
 */
import { getData } from '../../services/API';
import {
  employeeRoundHistoryUrl,
  getCensusReportUrl,
  getComplianceReportUrl,
  getEmpComplianceReportUrl,
  getEmployeeFreqUrl,
  getEmployeeRoundsReportUrl,
  getEmployeeRoundsUrl,
  getPatientRoundsReportUrl,
  getPatientRoundsUrl,
  patientHistorySearchUrl,
  patientRoundHistoryUrl
} from '../../services/API/endpoints';
import { processError, processResponse } from './utils/transformations';
import { date } from '../../utils/helpers';
import { setToastOptions, slideIn } from '../actions';
import {
  cleanEmployeeFrequencyReportDownload,
  cleanEmployeeRoundsReportDownload,
  cleanPatientRoundsReportDownload
} from './utils/cleaners';
/**
 * Global Type Definition Imports
 */
import {
  CensusReport,
  EmpFreqReport,
  EmpRoundsReport,
  PatientRoundsReport
} from '../../config/interfaces';

interface ISearchPatientHistoryPayload {
  facilityId: string;
  patientSearch: string;
}

interface IGetPatientRoundHistoryPayload {
  facilityId: string;
  patientId: string;
}

interface IGetEmployeeRoundHistoryPayload {
  employeeId: string;
  roundingTypes?: string[];
}

interface IGetComplianceReportPayload {
  startDay: string;
  endDay: string;
  facilityId: string;
}

interface IGetPatientRoundsReportDownloadPayload {
  facilityId: string;
  unitId: string[];
  query: string;
  roundedBy: string | string[];
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  isDownload?: boolean;
}

interface IGetPatientRoundsReportPayload
  extends IGetPatientRoundsReportDownloadPayload {
  page: number;
}

interface IGetCensusReportDownloadPayload {
  facilityId: string;
  unitId: string[];
  patient: string;
  user34s: string[];
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  status?: string;
  isDownload?: boolean;
}
interface IGetCensusReportPayload extends IGetCensusReportDownloadPayload {
  page: number;
  limit: number;
}

interface IGetEmployeeRoundsReportDownloadPayload {
  facilityId: string;
  query: string;
  roundedBy: string | string[];
  templateName: string;
  departments?: string;
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
  isDownload?: boolean;
}
interface IGetEmployeeRoundsReportPayload
  extends IGetEmployeeRoundsReportDownloadPayload {
  page: number;
}

interface IGetEmployeeFreqReportDownloadPayload {
  facilityId: string;
  query: string;
  sortBy: string;
  sortOrder: string;
  startday: string;
  endday: string;
}
interface IGetEmployeeFreqReportPayload
  extends IGetEmployeeFreqReportDownloadPayload {
  page: number;
}

/**
 * RTK Query API slice for report features.
 *
 * @remarks
 * Provides endpoints for:
 * - Patient Rounds Report (`getPatientRoundsReport`)
 * - Employee Rounds Report (`getEmployeeRoundsReport`)
 * - Other report-related endpoints as defined
 *
 * @example
 * import { useGetPatientRoundsReportQuery } from 'src/redux/apis/Reports.api';
 * const { data } = useGetPatientRoundsReportQuery();
 */
export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: (operation: any) => operation,
  tagTypes: [
    'patient-inapp-report',
    'patient-compliance-report',
    'patient-rounds-report',
    'patient-census-report',
    'patient-round-history',
    'search-patient-round-history',
    'employee-inapp-report',
    'employee-compliance-report',
    'employee-rounds-report',
    'employee-frequency-report',
    'employee-round-history',
    'patient-rounds-report-export',
    'employee-rounds-report-export',
    'patient-census-report-export',
    'employee-frequency-report-export'
  ],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    /**
     * Patient Reports
     */
    getPatientComplianceReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetComplianceReportPayload) =>
        getData(
          `${getComplianceReportUrl}?facilityId=${payload.facilityId}&startDay=${payload.startDay}&endDay=${payload.endDay}`,
          {}
        ),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-compliance-report',
          id: payload.facilityId
        }
      ]
    }),
    getPatientRoundsReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientRoundsReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getPatientRoundsUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-rounds-report',
          id: payload.facilityId
        }
      ]
    }),
    getCensusReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetCensusReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getCensusReportUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-census-report',
          id: payload.facilityId
        }
      ]
    }),
    getPatientRoundHistory: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientRoundHistoryPayload) => {
        return getData(
          `${patientRoundHistoryUrl}?facilityId=${payload.facilityId}&patientId=${payload.patientId}`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-round-history',
          id: payload.patientId
        }
      ]
    }),
    getSearchPatientRoundHistory: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: ISearchPatientHistoryPayload) => {
        let url;
        if (payload.patientSearch.match(/\d+/g)) {
          url = `${patientHistorySearchUrl}?facilityId=${payload.facilityId}&mrn=${payload.patientSearch}&page=-1`;
        } else {
          url = `${patientHistorySearchUrl}?facilityId=${payload.facilityId}&name=${payload.patientSearch}&page=-1`;
        }
        return getData(url, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'search-patient-round-history',
          id: payload.patientSearch
        }
      ]
    }),
    /**
     * Employee Reports
     */
    getEmployeeComplianceReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetComplianceReportPayload) =>
        getData(
          `${getEmpComplianceReportUrl}?facilityId=${payload.facilityId}&startDay=${payload.startDay}&endDay=${payload.endDay}`,
          {}
        ),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-compliance-report',
          id: payload.facilityId
        }
      ]
    }),
    getEmployeeRoundsReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeRoundsReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getEmployeeRoundsUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-rounds-report',
          id: payload.facilityId
        }
      ]
    }),
    getEmployeeFrequencyReport: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeFreqReportPayload) => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getEmployeeFreqUrl}?${params}`, {});
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-frequency-report',
          id: payload.facilityId
        }
      ]
    }),
    getEmployeeRoundHistory: builder.query({
      keepUnusedDataFor: 0,
      query: (payload: IGetEmployeeRoundHistoryPayload) => {
        return getData(
          `${employeeRoundHistoryUrl}?employeeId=${payload.employeeId}${
            payload.roundingTypes?.length
              ? `&roundingTypes=${payload.roundingTypes}`
              : ''
          }`,
          {}
        );
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-round-history',
          id: payload.employeeId
        }
      ]
    }),
    /**
     * Export Endpoints
     */
    getPatientRoundsReportDownload: builder.query<
      PatientRoundsReport[],
      IGetPatientRoundsReportDownloadPayload
    >({
      keepUnusedDataFor: 0,
      query: (payload: IGetPatientRoundsReportDownloadPayload) => {
        const params = new URLSearchParams({
          ...payload,
          isExport: 'true'
        } as any).toString();
        return getData(`${getPatientRoundsReportUrl}?${params}`, {});
      },
      transformResponse: response => {
        return cleanPatientRoundsReportDownload(response);
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-rounds-report-export',
          id: 'LIST'
        },
        { type: 'patient-rounds-report-export', id: JSON.stringify(payload) }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    }),
    getEmployeeRoundsReportDownload: builder.query<
      EmpRoundsReport[],
      IGetEmployeeRoundsReportDownloadPayload
    >({
      keepUnusedDataFor: 0,
      query: payload => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getEmployeeRoundsReportUrl}?${params}`, {});
      },
      transformResponse: (response, _meta, arg) => {
        return cleanEmployeeRoundsReportDownload(response, arg);
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-rounds-report-export',
          id: 'LIST'
        },
        { type: 'employee-rounds-report-export', id: JSON.stringify(payload) }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    }),
    getCensusReportDownload: builder.query<
      CensusReport[],
      IGetCensusReportDownloadPayload
    >({
      keepUnusedDataFor: 0,
      query: payload => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(`${getCensusReportUrl}?${params}`, {});
      },
      transformResponse: response => {
        const censusReportList =
          response?.map((census: any) => {
            return {
              censusDate: date.formatDateDashtoSlash(census?.reportDate),
              unit: census?.unitName,
              unitId: census?.unitId,
              room: census?.room || '',
              patient: `${census?.firstName} ${census?.lastName}` || '',
              mrn: census?.mrn || '',
              roundDate: census.rounds[0]?.roundDate || '',
              roundedBy:
                census?.status === 'none'
                  ? ''
                  : census?.rounds.length > 0
                  ? `${census.rounds[0]?.userFirstName} ${census.rounds[0]?.userLastName}`
                  : '',
              status:
                census?.status === 'none'
                  ? 'Not Rounded'
                  : census?.status === 'rounded'
                  ? 'Rounded'
                  : 'Unable To Round'
            };
          }) || [];
        return censusReportList;
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'patient-census-report-export',
          id: 'LIST'
        },
        { type: 'patient-census-report-export', id: JSON.stringify(payload) }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    }),
    getFrequencyReportDownload: builder.query<
      EmpFreqReport[],
      IGetEmployeeFreqReportDownloadPayload
    >({
      keepUnusedDataFor: 0,
      query: payload => {
        const params = new URLSearchParams(payload as any).toString();
        return getData(
          `${getEmployeeRoundsReportUrl}?${params}&isFrequencyReport=true`,
          {}
        );
      },
      transformResponse: (response, _meta, arg) => {
        return cleanEmployeeFrequencyReportDownload(response, arg);
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        {
          type: 'employee-frequency-report-export',
          id: 'LIST'
        },
        {
          type: 'employee-frequency-report-export',
          id: JSON.stringify(payload)
        }
      ],
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(
            setToastOptions({
              msg: 'Failed to retrieve report!',
              feedback: 'error',
              duration: 1200,
              speed: 350,
              question: true
            })
          );
          dispatch(slideIn());
        }
      }
    })
  })
});

export const {
  useGetPatientComplianceReportQuery,
  useLazyGetPatientComplianceReportQuery,
  useGetPatientRoundsReportQuery,
  useLazyGetPatientRoundsReportQuery,
  useGetCensusReportQuery,
  useLazyGetCensusReportQuery,
  useGetPatientRoundHistoryQuery,
  useLazyGetPatientRoundHistoryQuery,
  useGetEmployeeComplianceReportQuery,
  useGetSearchPatientRoundHistoryQuery,
  useLazyGetSearchPatientRoundHistoryQuery,
  useLazyGetEmployeeComplianceReportQuery,
  useGetEmployeeRoundsReportQuery,
  useLazyGetEmployeeRoundsReportQuery,
  useGetEmployeeFrequencyReportQuery,
  useLazyGetEmployeeFrequencyReportQuery,
  useGetEmployeeRoundHistoryQuery,
  useLazyGetEmployeeRoundHistoryQuery,
  useGetPatientRoundsReportDownloadQuery,
  useLazyGetPatientRoundsReportDownloadQuery,
  useGetEmployeeRoundsReportDownloadQuery,
  useLazyGetEmployeeRoundsReportDownloadQuery,
  useGetCensusReportDownloadQuery,
  useLazyGetCensusReportDownloadQuery,
  useGetFrequencyReportDownloadQuery,
  useLazyGetFrequencyReportDownloadQuery
} = reportsApi;
